import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Stack,
  Chip,
  Alert,
  Switch,
  FormControlLabel,
  FormControl,
  RadioGroup,
  Radio,
  CircularProgress,
} from "@mui/material";
import EventIcon from "@mui/icons-material/Event";
import SyncIcon from "@mui/icons-material/Sync";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "react-query";
import dayjs from "dayjs";
import {
  getGoogleCalendarOauthUrl,
  getGoogleCalendarStatus,
  disconnectGoogleCalendar,
  patchGoogleCalendarSettings,
} from "../../api/client";
import { getApiErrorMessage } from "../../utils/apiErrorMessage";
import { runGoogleCalendarSyncToCompletion } from "../../utils/googleCalendarSync";
import ApiErrorAlertBody, { resolveApiErrorNotice } from "../../components/ApiErrorAlertBody";
import ConfirmDialog from "../../components/common/ConfirmDialog";
import { AuthContext } from "../../context/AuthContext";

function unwrapStatus(res) {
  if (res && typeof res === "object" && "data" in res) return res.data;
  return res;
}

export default function GoogleCalendarProfileSection() {
  const { t } = useTranslation("profile");
  const queryClient = useQueryClient();
  const { userData } = useContext(AuthContext);
  const [actionError, setActionError] = useState("");
  const [actionNotice, setActionNotice] = useState(null);

  const reportActionError = (err, fallback) => {
    const notice = resolveApiErrorNotice(err, fallback);
    setActionNotice(notice);
    setActionError(getApiErrorMessage(err, notice.message));
  };
  const [disconnectOpen, setDisconnectOpen] = useState(false);
  const [importedEventsAction, setImportedEventsAction] = useState("keep");
  const [progress, setProgress] = useState(null);
  const resumeRef = useRef(false);

  const { data: statusRaw, isLoading } = useQuery(
    ["google-calendar-status"],
    async () => {
      const r = await getGoogleCalendarStatus();
      return unwrapStatus(r) ?? {};
    },
    { refetchInterval: (data) => (data?.syncing ? 4_000 : 60_000) }
  );

  const status = statusRaw || {};
  const connected = Boolean(status.connected);
  const requiresLocalUser = Boolean(status.requiresLocalUser);
  const accountEmail = String(userData?.email || "").trim().toLowerCase();

  const connectMutation = useMutation(
    async () => {
      setActionError("");
      setActionNotice(null);
      const r = await getGoogleCalendarOauthUrl();
      const url = r?.url || unwrapStatus(r)?.url;
      if (!url || typeof url !== "string") throw new Error(t("googleCalendar.errors.noUrl"));
      window.location.assign(url);
    },
    {
      onError: (err) => {
        const code = err?.code || err?.response?.data?.code || err?.data?.code;
        if (code === "google_calendar_no_local_user") {
          const message = t("googleCalendar.errors.noLocalUser");
          setActionError(message);
          setActionNotice({ message, action: null });
          return;
        }
        reportActionError(err, t("googleCalendar.errors.connectFailed"));
      },
    }
  );

  const disconnectMutation = useMutation(
    async (removeImported) => {
      setActionError("");
      setActionNotice(null);
      await disconnectGoogleCalendar({ removeImported: Boolean(removeImported) });
    },
    {
      onSuccess: () => {
        setDisconnectOpen(false);
        setImportedEventsAction("keep");
        queryClient.invalidateQueries(["google-calendar-status"]);
        queryClient.invalidateQueries(["user-agenda"]);
      },
      onError: (err) => {
        reportActionError(err, t("googleCalendar.errors.disconnectFailed"));
      },
    }
  );

  const syncMutation = useMutation(
    async ({ forceFullSync = false } = {}) => {
      setActionError("");
      setActionNotice(null);
      return runGoogleCalendarSyncToCompletion({
        forceFullSync,
        onChunk: (chunk) => {
          setProgress({
            count: Number(chunk?.processed) || Number(chunk?.syncProcessed) || 0,
            syncing: Boolean(chunk?.syncing) || chunk?.complete === false,
            background: Boolean(chunk?.background),
          });
          queryClient.invalidateQueries(["user-agenda"]);
        },
      });
    },
    {
      onSuccess: (result) => {
        queryClient.invalidateQueries(["google-calendar-status"]);
        queryClient.invalidateQueries(["user-agenda"]);
        if (result?.background) {
          setProgress({
            count: Number(result.processed) || 0,
            syncing: true,
            background: true,
          });
          return;
        }
        setProgress(null);
      },
      onError: (err) => {
        setProgress(null);
        const code = err?.code || err?.response?.data?.code || err?.data?.code;
        if (code === "google_calendar_needs_reauth") {
          const message = t("googleCalendar.needsReauth");
          setActionError(message);
          setActionNotice({ message, action: null });
          queryClient.invalidateQueries(["google-calendar-status"]);
          return;
        }
        reportActionError(err, t("googleCalendar.errors.syncFailed"));
      },
    }
  );

  const toggleSyncMutation = useMutation(
    async (enabled) => {
      setActionError("");
      setActionNotice(null);
      await patchGoogleCalendarSettings({ syncEnabled: enabled });
    },
    {
      onSuccess: () => queryClient.invalidateQueries(["google-calendar-status"]),
      onError: (err) => {
        reportActionError(err, t("googleCalendar.errors.settingsFailed"));
      },
    }
  );

  const handleConnect = useCallback(() => {
    connectMutation.mutate();
  }, [connectMutation]);

  const openDisconnectDialog = useCallback(() => {
    setActionError("");
    setActionNotice(null);
    setImportedEventsAction("keep");
    setDisconnectOpen(true);
  }, []);

  const closeDisconnectDialog = useCallback(() => {
    if (disconnectMutation.isLoading) return;
    setDisconnectOpen(false);
  }, [disconnectMutation.isLoading]);

  const confirmDisconnect = useCallback(() => {
    disconnectMutation.mutate(importedEventsAction === "remove");
  }, [disconnectMutation, importedEventsAction]);

  const lastSyncLabel = status.lastSyncAt
    ? dayjs(String(status.lastSyncAt).replace(" ", "T")).format("DD/MM/YYYY HH:mm")
    : null;
  const syncing =
    syncMutation.isLoading || Boolean(status.syncing) || Boolean(progress?.syncing);
  const progressCount = Number(progress?.count) || Number(status.syncProcessed) || 0;

  useEffect(() => {
    if (!connected || status.needsReauth || !status.syncing || resumeRef.current) return;
    resumeRef.current = true;
    syncMutation.mutate({ forceFullSync: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- retoma uma vez por montagem
  }, [connected, status.needsReauth, status.syncing]);

  return (
    <Paper sx={{ p: 3, borderRadius: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <EventIcon color="primary" />
        <Typography variant="h6">{t("googleCalendar.section")}</Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t("googleCalendar.intro")}
      </Typography>

      {actionError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          <ApiErrorAlertBody
            message={actionNotice?.message || actionError}
            action={actionNotice?.action}
          />
        </Alert>
      ) : null}

      {syncing ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" fontWeight={700} component="div">
            {t("googleCalendar.syncing")}
          </Typography>
          <Typography variant="body2" component="div">
            {progress?.background
              ? t("googleCalendar.syncingBackground")
              : t("googleCalendar.syncingHint", { count: progressCount })}
          </Typography>
        </Alert>
      ) : null}

      {!isLoading && requiresLocalUser ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t("googleCalendar.requiresLocalUser", { email: accountEmail || "—" })}
        </Alert>
      ) : null}

      {isLoading ? (
        <CircularProgress size={24} />
      ) : (
        <Stack spacing={2}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Chip
              size="small"
              color={connected ? "success" : "default"}
              label={connected ? t("googleCalendar.connected") : t("googleCalendar.disconnected")}
            />
            {connected && status.googleEmail ? (
              <Typography variant="body2" color="text.secondary">
                {status.googleEmail}
              </Typography>
            ) : null}
          </Stack>

          {connected ? (
            <>
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(status.syncEnabled)}
                    onChange={(e) => toggleSyncMutation.mutate(e.target.checked)}
                    disabled={toggleSyncMutation.isLoading}
                  />
                }
                label={t("googleCalendar.syncEnabled")}
              />
              {lastSyncLabel ? (
                <Typography variant="caption" color="text.secondary">
                  {t("googleCalendar.lastSync", { date: lastSyncLabel })}
                </Typography>
              ) : null}
              {status.needsReauth ? (
                <Alert severity="warning">
                  {t("googleCalendar.needsReauth", {
                    defaultValue:
                      "Reconecte o Google Agenda para habilitar calendários compartilhados e disponibilidade da equipe.",
                  })}
                </Alert>
              ) : null}
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                {status.needsReauth ? (
                  <Button
                    type="button"
                    variant="contained"
                    startIcon={
                      connectMutation.isLoading ? <CircularProgress size={16} color="inherit" /> : <EventIcon />
                    }
                    onClick={handleConnect}
                    disabled={connectMutation.isLoading}
                    sx={{ textTransform: "none", fontWeight: 700 }}
                  >
                    {t("googleCalendar.reconnect", { defaultValue: t("googleCalendar.connect") })}
                  </Button>
                ) : (
                  <Button
                    variant="outlined"
                    startIcon={syncing ? <CircularProgress size={16} /> : <SyncIcon />}
                    onClick={() => syncMutation.mutate({ forceFullSync: true })}
                    disabled={syncMutation.isLoading || !status.syncEnabled}
                  >
                    {syncing ? t("googleCalendar.syncing") : t("googleCalendar.syncNow")}
                  </Button>
                )}
                <Button
                  variant="text"
                  color="inherit"
                  startIcon={<LinkOffIcon />}
                  onClick={openDisconnectDialog}
                  disabled={disconnectMutation.isLoading}
                >
                  {t("googleCalendar.disconnect")}
                </Button>
              </Stack>
            </>
          ) : (
            <Button
              type="button"
              variant="contained"
              startIcon={connectMutation.isLoading ? <CircularProgress size={16} color="inherit" /> : <EventIcon />}
              onClick={handleConnect}
              disabled={connectMutation.isLoading || requiresLocalUser}
              sx={{ alignSelf: "flex-start", textTransform: "none", fontWeight: 700 }}
            >
              {t("googleCalendar.connect")}
            </Button>
          )}

          <Box component="ul" sx={{ m: 0, pl: 2.5, color: "text.secondary" }}>
            <Typography component="li" variant="caption" sx={{ mb: 0.5 }}>
              {t("googleCalendar.bulletManualOnly")}
            </Typography>
            <Typography component="li" variant="caption">
              {t("googleCalendar.bulletXproOnly")}
            </Typography>
          </Box>
        </Stack>
      )}

      <ConfirmDialog
        open={disconnectOpen}
        title={t("googleCalendar.disconnectDialog.title")}
        maxWidth="sm"
        fullWidth
        cancelText={t("googleCalendar.disconnectDialog.cancel")}
        confirmText={t("googleCalendar.disconnectDialog.confirm")}
        confirmColor="error"
        loading={disconnectMutation.isLoading}
        onClose={closeDisconnectDialog}
        onConfirm={confirmDisconnect}
        content={
          <Box sx={{ pt: 0.5 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t("googleCalendar.disconnectDialog.intro")}
            </Typography>
            <FormControl component="fieldset" fullWidth>
              <RadioGroup
                value={importedEventsAction}
                onChange={(e) => setImportedEventsAction(e.target.value)}
              >
                <FormControlLabel
                  value="keep"
                  control={<Radio />}
                  disabled={disconnectMutation.isLoading}
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {t("googleCalendar.disconnectDialog.keep")}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t("googleCalendar.disconnectDialog.keepHint")}
                      </Typography>
                    </Box>
                  }
                  sx={{ alignItems: "flex-start", mb: 1.5 }}
                />
                <FormControlLabel
                  value="remove"
                  control={<Radio />}
                  disabled={disconnectMutation.isLoading}
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {t("googleCalendar.disconnectDialog.remove")}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t("googleCalendar.disconnectDialog.removeHint")}
                      </Typography>
                    </Box>
                  }
                  sx={{ alignItems: "flex-start" }}
                />
              </RadioGroup>
            </FormControl>
          </Box>
        }
      />
    </Paper>
  );
}
